export default (config = {}) => ({
  selectedVariantId: config.selectedVariantId ?? null,
  minCustomAmount: config.minCustomAmount ?? 20,
  maxCustomAmount: config.maxCustomAmount ?? 500,
  selectedAmount: config.selectedAmount ?? null,
  allVariants: config.allVariants ?? [],
  selectedDesign: config.selectedDesign ?? '',
  sendTo: 'recipient',
  sendWhen: 'immediately',
  showOtherAmount: false,
  customAmount: '',
  recipientEmail: '',
  recipientEmailError: null,
  confirmEmail: '',
  confirmEmailError: null,
  recipientNameError: null,
  senderNameError: null,
  recipientName: '',
  senderName: '',
  message: '',
  characterCount: 200,
  sendDate: '',
  minDate: '',
  currentMonth: null,
  currentYear: null,
  today: null,
  selectedDateObj: null,
  dayLabels: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  submitting: false,
  formError: null,

  init() {
    const tomorrow = new Date()
    tomorrow.setDate(tomorrow.getDate() + 1)

    const year = tomorrow.getFullYear()
    const month = String(tomorrow.getMonth() + 1).padStart(2, '0')
    const day = String(tomorrow.getDate()).padStart(2, '0')
    const isoTomorrow = `${year}-${month}-${day}`

    this.minDate = isoTomorrow
    this.sendDate = isoTomorrow
    this.today = new Date()
    this.currentMonth = tomorrow.getMonth()
    this.currentYear = tomorrow.getFullYear()
  },

  get designs() {
    const seen = new Set()
    const result = []
    for (const v of this.allVariants) {
      if (!seen.has(v.design)) {
        seen.add(v.design)
        result.push(v.design)
      }
    }
    return result
  },

  get selectedDesignIndex() {
    return this.designs.indexOf(this.selectedDesign)
  },

  get denominationsForSelectedDesign() {
    return this.allVariants.filter(v => v.design === this.selectedDesign)
  },

  get canSubmit() {
    if (!this.selectedVariantId) return false
    if (this.showOtherAmount) {
      const amount = parseFloat(this.customAmount)
      if (!amount || amount < this.minCustomAmount || amount > this.maxCustomAmount) return false
    }
    if (this.sendTo === 'recipient') {
      if (!this.recipientEmail) return false
      if (this.recipientEmailError) return false
      if (!this.confirmEmail || this.confirmEmail !== this.recipientEmail) return false
      if (this.confirmEmailError) return false
      if (!this.recipientName?.trim()) return false
      if (!this.senderName?.trim()) return false
    }
    return true
  },

  get displayAmount() {
    if (this.showOtherAmount) {
      const amount = parseFloat(this.customAmount)
      if (!amount || Number.isNaN(amount)) return null
      return amount
    }
    return this.selectedAmount
  },

  get calendarDays() {
    const days = []

    if (this.currentYear === null || this.currentMonth === null) return days

    const firstOfMonth = new Date(this.currentYear, this.currentMonth, 1)
    const startDay = firstOfMonth.getDay()
    const startDate = new Date(this.currentYear, this.currentMonth, 1 - startDay)

    for (let i = 0; i < 42; i += 1) {
      const date = new Date(startDate)
      date.setDate(startDate.getDate() + i)

      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const iso = `${year}-${month}-${day}`
      const inCurrentMonth = date.getMonth() === this.currentMonth
      const disabled = iso < this.minDate

      days.push({
        date,
        iso,
        label: date.getDate(),
        inCurrentMonth,
        disabled,
      })
    }

    return days
  },

  get calendarWeeks() {
    const days = this.calendarDays
    const weeks = []

    for (let i = 0; i < days.length; i += 7) {
      weeks.push(days.slice(i, i + 7))
    }

    return weeks
  },

  get formattedMonthYear() {
    if (this.currentYear === null || this.currentMonth === null) return ''
    const monthDate = new Date(this.currentYear, this.currentMonth, 1)
    return monthDate.toLocaleDateString(undefined, {
      month: 'long',
      year: 'numeric',
    })
  },

  get formattedSendDateMessage() {
    if (!this.sendDate) return ''
    const [year, month, day] = this.sendDate.split('-').map(Number)
    const date = new Date(year, month - 1, day)

    return date.toLocaleDateString(undefined, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    })
  },

  selectDesign(design) {
    if (this.selectedDesign === design) return

    const currentVariant = this.allVariants.find(v => v.id === this.selectedVariantId)
    const currentDenomination = currentVariant?.denomination

    this.selectedDesign = design

    const matchingVariant =
      this.allVariants.find(v => v.design === design && v.denomination === currentDenomination) ??
      this.allVariants.find(v => v.design === design)

    if (matchingVariant) {
      this.selectedVariantId = matchingVariant.id
      this.selectedAmount = matchingVariant.price
      this.showOtherAmount = false
    }
  },

  selectDenomination(id, amount) {
    this.selectedVariantId = id
    this.selectedAmount = amount
    this.showOtherAmount = false
  },

  toggleOtherAmount() {
    this.showOtherAmount = !this.showOtherAmount
    if (!this.showOtherAmount) {
      this.customAmount = ''
    }
  },

  validateRecipientEmail() {
    this.$nextTick(() => {
      const email = this.recipientEmail?.trim()
      if (!email) {
        this.recipientEmailError = null
        return
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      this.recipientEmailError = emailRegex.test(email) ? null : 'Please enter a valid email address'
    })
  },

  validateConfirmEmail() {
    this.$nextTick(() => {
      if (!this.confirmEmail?.trim()) {
        this.confirmEmailError = null
        return
      }
      if (this.confirmEmail !== this.recipientEmail) {
        this.confirmEmailError = 'Fields are not the same.'
      } else {
        this.confirmEmailError = null
      }
    })
  },

  selectDate(day) {
    if (day.disabled) return
    this.sendDate = day.iso
    this.selectedDateObj = day.date
  },

  goToPrevMonth() {
    if (this.currentYear === null || this.currentMonth === null) return
    const newMonth = this.currentMonth - 1
    if (newMonth < 0) {
      this.currentMonth = 11
      this.currentYear -= 1
    } else {
      this.currentMonth = newMonth
    }
  },

  goToNextMonth() {
    if (this.currentYear === null || this.currentMonth === null) return
    const newMonth = this.currentMonth + 1
    if (newMonth > 11) {
      this.currentMonth = 0
      this.currentYear += 1
    } else {
      this.currentMonth = newMonth
    }
  },

  resetForm(formEl) {
    if (formEl) {
      formEl.reset()
    }

    this.sendTo = 'recipient'
    this.sendWhen = 'immediately'
    this.showOtherAmount = false
    this.customAmount = ''

    this.recipientEmail = ''
    this.recipientEmailError = null
    this.confirmEmail = ''
    this.confirmEmailError = null
    this.recipientName = ''
    this.recipientNameError = null
    this.senderName = ''
    this.senderNameError = null

    this.message = ''
    this.characterCount = 200

    this.formError = null

    if (this.minDate) {
      this.sendDate = this.minDate
      this.selectedDateObj = null
    }
  },

  async handleSubmit(event) {
    this.formError = null
    const REQUIRED_MESSAGE = 'This is a required field.'

    if (this.sendTo === 'recipient') {
      this.recipientEmailError = null
      this.confirmEmailError = null
      this.recipientNameError = null
      this.senderNameError = null

      const email = this.recipientEmail?.trim()
      const confirm = this.confirmEmail?.trim()

      if (!email) {
        this.recipientEmailError = REQUIRED_MESSAGE
      } else {
        // format validation only when non-empty
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        this.recipientEmailError = emailRegex.test(email)
          ? null
          : 'Please enter a valid email address'
      }

      if (!confirm) {
        this.confirmEmailError = REQUIRED_MESSAGE
      } else if (confirm !== email) {
        this.confirmEmailError = 'Fields are not the same.'
      }

      if (!this.recipientName?.trim()) {
        this.recipientNameError = REQUIRED_MESSAGE
      }

      if (!this.senderName?.trim()) {
        this.senderNameError = REQUIRED_MESSAGE
      }

      if (
        this.recipientEmailError ||
        this.confirmEmailError ||
        this.recipientNameError ||
        this.senderNameError
      ) {
        return
      }
    }

    if (!this.canSubmit) return

    const formEl = event.target.closest('form')
    if (!formEl) return

    this.submitting = true
    const formData = new FormData(formEl)

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      })

      if (!response.ok) {
        const data = await response.json()
        this.formError = data.description || 'Something went wrong. Please try again.'
        return
      }

      this.resetForm(formEl)
      this.$store.cart.getCardItems()
      this.$store.cart.openMiniCart()
    } catch {
      this.formError = 'Something went wrong. Please try again.'
    } finally {
      this.submitting = false
    }
  },
})
